import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, signInWithOkta, devSignIn } from '../firebase';
import { COLLECTIONS } from '../collections';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    isAdmin: false,
    isCommunicator: false,
    isPeoplePartner: false,
    ppAssignedEmails: [],
    loading: true,
  });

  /* ─── Impersonation state (admin-only) ─── */
  const [impersonating, setImpersonating] = useState(null); // { email, name }

  /* Determine role after sign-in */
  const resolveRole = useCallback(async (fbUser) => {
    if (!fbUser) {
      setState({ user: null, isAdmin: false, isCommunicator: false, isPeoplePartner: false, ppAssignedEmails: [], loading: false });
      return;
    }
    const email = fbUser.email;
    try {
      let isAdmin = false;
      let isCommunicator = false;
      let isPeoplePartner = false;
      let ppAssignedEmails = [];

      try {
        const roleSnap = await getDoc(doc(db, COLLECTIONS.userRoles, email));
        if (roleSnap.exists()) {
          const roles = roleSnap.data();
          isAdmin = !!roles.isAdmin;
          isCommunicator = !!roles.isCommunicator;
          isPeoplePartner = !!roles.isPeoplePartner;
          ppAssignedEmails = roles.ppAssignedEmails || [];
        }
      } catch (_e) {
        console.warn('Role lookup failed', _e);
      }

      setState({ user: fbUser, isAdmin, isCommunicator, isPeoplePartner, ppAssignedEmails, loading: false });
    } catch (err) {
      console.error('Role resolution failed', err);
      setState({ user: fbUser, isAdmin: false, isCommunicator: false, isPeoplePartner: false, ppAssignedEmails: [], loading: false });
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, resolveRole);
    return () => unsub();
  }, [resolveRole]);

  const signIn = useCallback(() => signInWithOkta(), []);
  const signInDev = useCallback((email) => devSignIn(email), []);
  const signOut = useCallback(async () => {
    setImpersonating(null);
    await fbSignOut(auth);
  }, []);

  /* Re-check roles (e.g., after admin adds comps) */
  const refreshRole = useCallback(() => {
    if (state.user) resolveRole(state.user);
  }, [state.user, resolveRole]);

  /* ─── Impersonation helpers (admin or People Partner) ─── */
  const startImpersonating = useCallback((email, name) => {
    if (!state.isAdmin && !state.isPeoplePartner) return;
    // People Partners can only impersonate their assigned users
    if (state.isPeoplePartner && !state.isAdmin && !state.ppAssignedEmails.includes(email)) return;
    setImpersonating({ email, name });
  }, [state.isAdmin, state.isPeoplePartner, state.ppAssignedEmails]);

  const stopImpersonating = useCallback(() => {
    setImpersonating(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      signIn,
      signInDev,
      signOut,
      refreshRole,
      impersonating,
      startImpersonating,
      stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
